import { format } from 'date-fns';
import {
    CaseDocumentSummary,
    CaseReport,
} from '../../../services/reportService';
import { calculateCptCodes } from '../../documents/form/views/cpt/CptCodesView';
import {
    CASE_CONTEXT_SCHEMA_KEY,
    DEVICE_PREPARATION_SCHEMA_KEY,
    getCaseContextValues,
    getDocumentBySchemaKey,
    INITIAL_EVALUATION_SCHEMA_KEY,
    POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
    RISK_BENEFIT_SCHEMA_KEY,
} from '../cpt/utils/caseWorkspaceUtils';
import {
    build76016StatusList,
    build76018StatusList,
    build76019StatusList,
} from '../cpt/utils/cptEligibility';

export type ManageReportEligibleCode =
    | 'CPT 76014'
    | 'CPT 76015'
    | 'CPT 76016'
    | 'CPT 76018'
    | 'CPT 76019';

export interface ManageReportDeleteState {
    canDelete: boolean;
    reason?: string;
}

export interface ManageReportCaseStatusMeta {
    label: 'Draft' | 'Completed' | 'Auto-saved';
    className: string;
}

const DATE_FORMAT = 'MMM d, yyyy';
const DELETE_DISABLED_REASON = 'Completed reports cannot be deleted.';
const COMPLETED_CASE_STATUSES = new Set(['completed', 'final', 'archived']);

const getReportDocuments = (caseItem: CaseReport) =>
    caseItem.documents.filter(
        (document) => document.schemaKey !== CASE_CONTEXT_SCHEMA_KEY
    );

const countCode = (codes: Array<{ code: string }>, code: '76014' | '76015') =>
    codes.filter((entry) => entry.code === code).length;

const getInitialEvaluationDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null =>
    getDocumentBySchemaKey(caseItem, INITIAL_EVALUATION_SCHEMA_KEY);

export const getRiskBenefitDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null =>
    getDocumentBySchemaKey(caseItem, RISK_BENEFIT_SCHEMA_KEY);

const getPositioningImmobilizationDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null =>
    getDocumentBySchemaKey(caseItem, POSITIONING_IMMOBILIZATION_SCHEMA_KEY);

const getDevicePreparationDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null =>
    getDocumentBySchemaKey(caseItem, DEVICE_PREPARATION_SCHEMA_KEY);

const getTotalLoggedMinutes = (caseItem: CaseReport): number => {
    const initialEvaluationDocument = getInitialEvaluationDocument(caseItem);
    const allTasks = Array.isArray(initialEvaluationDocument?.data?.allTasks)
        ? initialEvaluationDocument.data.allTasks
        : [];

    return allTasks.reduce((sum: number, task: any) => {
        return sum + (Number(task?.minutes) || 0);
    }, 0);
};

export const getCaseSummary = (caseItem: CaseReport) => {
    const caseContextData =
        getDocumentBySchemaKey(caseItem, CASE_CONTEXT_SCHEMA_KEY)?.data || {};
    const parts: string[] = [];

    if (caseContextData.exam) parts.push(caseContextData.exam);
    if (caseContextData.clinicalIndication) {
        parts.push(caseContextData.clinicalIndication);
    }

    return parts.join(' • ') || 'No details documented yet';
};

export const getCaseSummaryParts = (caseItem: CaseReport) => {
    const caseContextData =
        getDocumentBySchemaKey(caseItem, CASE_CONTEXT_SCHEMA_KEY)?.data || {};

    return {
        exam: ((caseContextData.exam as string) || '').trim(),
        clinicalIndication: (
            (caseContextData.clinicalIndication as string) || ''
        ).trim(),
    };
};

export const getPrimaryPreviewDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null => {
    const initialDocument = getInitialEvaluationDocument(caseItem);
    const riskBenefitDocument = getRiskBenefitDocument(caseItem);

    return initialDocument || riskBenefitDocument || null;
};

export const getCaseContextPreviewDocument = (
    caseItem: CaseReport
): CaseDocumentSummary | null =>
    getDocumentBySchemaKey(caseItem, CASE_CONTEXT_SCHEMA_KEY);

export const getDeleteState = (
    caseItem: CaseReport
): ManageReportDeleteState => {
    const reportDocuments = getReportDocuments(caseItem);

    if (reportDocuments.some((document) => document.status !== 'draft')) {
        return {
            canDelete: false,
            reason: DELETE_DISABLED_REASON,
        };
    }

    return { canDelete: true };
};

export const getCaseStatusMeta = (
    caseItem: CaseReport
): ManageReportCaseStatusMeta => {
    const isCompleted =
        COMPLETED_CASE_STATUSES.has(caseItem.status || '') ||
        getReportDocuments(caseItem).some(
            (document) => document.status !== 'draft'
        );

    if (isCompleted) {
        return {
            label: 'Completed',
            className:
                'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-800 dark:text-emerald-100/90',
        };
    }

    if (caseItem.status === 'auto-saved') {
        return {
            label: 'Auto-saved',
            className:
                'border-sky-500/30 bg-sky-500/[0.08] text-sky-800 dark:text-sky-100/90',
        };
    }

    return {
        label: 'Draft',
        className:
            'border-amber-500/30 bg-amber-500/[0.08] text-amber-800 dark:text-amber-100/90',
    };
};

export const getCaseDateMeta = (caseItem: CaseReport) => {
    const caseContextValues = getCaseContextValues(caseItem);
    const scanDateValue =
        caseContextValues.scanDate ||
        getInitialEvaluationDocument(caseItem)?.data?.assessmentDate;

    return {
        scanDateValue,
        scanDateLabel: formatOptionalDate(scanDateValue),
        updatedAtLabel: formatOptionalDate(caseItem.updatedAt, 'Not available'),
    };
};

export const getCaseEligibilityStatuses = (
    caseItem: CaseReport
): ManageReportEligibleCode[] => {
    const totalLoggedMinutes = getTotalLoggedMinutes(caseItem);
    const computedCodes = calculateCptCodes(totalLoggedMinutes);
    const code76014Count = countCode(computedCodes, '76014');
    const code76015Count = countCode(computedCodes, '76015');

    const riskBenefitDocument = getRiskBenefitDocument(caseItem);
    const statusList = build76016StatusList(riskBenefitDocument?.data || {});
    const eligibleCodes: ManageReportEligibleCode[] = [];

    if (code76014Count > 0) {
        eligibleCodes.push('CPT 76014');
    }

    if (code76015Count > 0) {
        eligibleCodes.push('CPT 76015');
    }

    if (statusList.state === 'success') {
        eligibleCodes.push('CPT 76016');
    }

    const devicePreparationDocument = getDevicePreparationDocument(caseItem);
    const devicePreparationStatusList = build76018StatusList(
        devicePreparationDocument?.data || {}
    );

    if (devicePreparationStatusList.state === 'success') {
        eligibleCodes.push('CPT 76018');
    }

    const positioningDocument = getPositioningImmobilizationDocument(caseItem);
    const initialEvalDocument = getInitialEvaluationDocument(caseItem);
    const positioningStatusList = build76019StatusList(
        positioningDocument?.data || {},
        { initialImplants: initialEvalDocument?.data?.implants }
    );

    if (positioningStatusList.state === 'success') {
        eligibleCodes.push('CPT 76019');
    }

    return eligibleCodes;
};

export const formatOptionalDate = (
    value?: string | Date | null,
    fallback = 'Not set'
) => {
    if (!value) return fallback;

    const parsedDate = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return fallback;
    }

    return format(parsedDate, DATE_FORMAT);
};
